import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Edit,
  Eye,
  Trash2,
  AlertTriangle,
  Server,
  Database,
  Monitor,
  HardDrive,
  Cpu,
  Network,
} from 'lucide-react';
import { Asset } from '../../types';

interface AssetsTableProps {
  assets: Asset[];
  onViewAsset: (asset: Asset) => void;
  onEditAsset: (asset: Asset) => void;
  onDeleteAsset: (assetId: number) => void;
  getItemName: (codelistType: string, code: string) => string;
  visibleColumns: string[];
  sortConfig: {
    key: string | null;
    direction: 'asc' | 'desc';
  };
  onSort: (key: string) => void;
  columnFilters: Record<string, string>;
  onColumnFilter: (column: string, value: string) => void;
  onClearColumnFilter: (column: string) => void;
  // Bulk selection props
  selectedAssetIds: Set<number>;
  selectAllMode: 'none' | 'page' | 'all';
  totalFilteredAssets: number;
  onSelectAsset: (assetId: number, selected: boolean) => void;
  onSelectAll: (mode: 'none' | 'page' | 'all') => void;
}

// Column definitions with display names and sortable/filterable properties
export const COLUMN_DEFINITIONS = {
  name: { label: 'Název', sortable: true, filterable: true },
  description: { label: 'Popis', sortable: true, filterable: true },
  type: { label: 'Typ', sortable: true, filterable: true },
  asset_category: { label: 'Kategorie', sortable: true, filterable: true },
  classification: { label: 'Klasifikace', sortable: true, filterable: true },
  status: { label: 'Status', sortable: true, filterable: true },
  value_assessment: { label: 'Hodnota', sortable: true, filterable: true },
  confidentiality_level: { label: 'Důvěrnost', sortable: true, filterable: true },
  integrity_level: { label: 'Integrita', sortable: true, filterable: true },
  availability_level: { label: 'Dostupnost', sortable: true, filterable: true },
  location: { label: 'Umístění', sortable: true, filterable: true },
  owner_name: { label: 'Vlastník', sortable: true, filterable: true },
  custodian_name: { label: 'Správce', sortable: true, filterable: true },
  nis2_chapter: { label: 'NIS2 Kapitola', sortable: true, filterable: true }, // Keep for backward compatibility
  nis2_article: { label: 'NIS2 Article', sortable: true, filterable: true },
  nis2_letter: { label: 'NIS2 Letter', sortable: true, filterable: true },
  review_date: { label: 'Datum kontroly', sortable: true, filterable: false },
  review_period: { label: 'Review interval', sortable: true, filterable: true },
  created_at: { label: 'Vytvořeno', sortable: true, filterable: false },
  updated_at: { label: 'Upraveno', sortable: true, filterable: false },
  actions: { label: 'Akce', sortable: false, filterable: false },
};

const AssetsTable: React.FC<AssetsTableProps> = ({
  assets,
  onViewAsset,
  onEditAsset,
  onDeleteAsset,
  getItemName,
  visibleColumns,
  sortConfig,
  onSort,
  columnFilters,
  onColumnFilter,
  onClearColumnFilter,
  selectedAssetIds,
  selectAllMode,
  totalFilteredAssets,
  onSelectAsset,
  onSelectAll,
}) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'endpoints': return <Monitor className="h-4 w-4" />;
      case 'identity_and_access': return <Server className="h-4 w-4" />;
      case 'network_and_network_security': return <Network className="h-4 w-4" />;
      case 'data': return <Database className="h-4 w-4" />;
      case 'applications': return <Cpu className="h-4 w-4" />;
      case 'systems': return <HardDrive className="h-4 w-4" />;
      default: return <Server className="h-4 w-4" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'retired': return 'outline';
      default: return 'default';
    }
  };

  const getValueAssessmentBadgeVariant = (value: string) => {
    switch (value) {
      case 'critical': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ');
  };

  const formatReviewPeriod = (period: string) => {
    const periodOptions: { [key: string]: string } = {
      '1d': '1 den',
      '7d': '1 týden', 
      '14d': '2 týdny',
      '30d': '1 měsíc',
      '3m': '3 měsíce',
      '6m': '6 měsíců',
      '1y': '1 rok',
      '2y': '2 roky',
      '3y': '3 roky',
      '5y': '5 let'
    };
    return periodOptions[period] || period;
  };

  // Determine checkbox states
  const currentPageAssetIds = assets.filter(asset => asset.id).map(asset => asset.id!);
  
  const handleMasterCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Always select all filtered assets (not just current page)
      onSelectAll('all');
    } else {
      // Unselect all
      onSelectAll('none');
    }
  };

  const getMasterCheckboxState = (): boolean | 'indeterminate' => {
    if (selectAllMode === 'all') {
      return true;
    } else if (selectedAssetIds.size > 0) {
      // If any assets are selected, show indeterminate state
      return 'indeterminate';
    } else {
      return false;
    }
  };

  const renderCellContent = (asset: Asset, column: string) => {
    switch (column) {
      case 'name':
        return (
          <div className="flex items-center space-x-2">
            {getCategoryIcon(asset.asset_category || 'system')}
            <span className="font-medium">{asset.name}</span>
          </div>
        );
      case 'description':
        return <span className="max-w-xs truncate" title={asset.description}>{asset.description}</span>;
      case 'type':
        return <Badge variant="outline">{getItemName('asset_types_nis2', asset.type || '')}</Badge>;
      case 'asset_category':
        return asset.asset_category ? (
          <Badge variant="secondary">{getItemName('asset_categories', asset.asset_category)}</Badge>
        ) : '-';
      case 'classification':
        return <Badge variant="outline">{asset.classification}</Badge>;
      case 'status':
        return <Badge variant={getStatusBadgeVariant(asset.status)}>{asset.status}</Badge>;
      case 'value_assessment':
        return <Badge variant={getValueAssessmentBadgeVariant(asset.value_assessment)}>{asset.value_assessment}</Badge>;
      case 'confidentiality_level':
        return <Badge variant="outline">{asset.confidentiality_level}</Badge>;
      case 'integrity_level':
        return <Badge variant="outline">{asset.integrity_level}</Badge>;
      case 'availability_level':
        return <Badge variant="outline">{asset.availability_level}</Badge>;
      case 'location':
        return asset.location || '-';
      case 'owner_name':
        return asset.owner_first_name && asset.owner_last_name 
          ? `${asset.owner_first_name} ${asset.owner_last_name}` 
          : '-';
      case 'custodian_name':
        return asset.custodian_first_name && asset.custodian_last_name 
          ? `${asset.custodian_first_name} ${asset.custodian_last_name}` 
          : '-';
      case 'nis2_chapter':
        return asset.nis2_article && asset.nis2_letter 
          ? `${getItemName('nis2_articles', asset.nis2_article)} - ${getItemName('nis2_letters', asset.nis2_letter)}`
          : '-';
      case 'nis2_article':
        return asset.nis2_article ? getItemName('nis2_articles', asset.nis2_article) : '-';
      case 'nis2_letter':
        return asset.nis2_letter ? getItemName('nis2_letters', asset.nis2_letter) : '-';
      case 'review_date':
        return asset.review_date ? (
          <div className="flex items-center space-x-1">
            <span>{formatDate(asset.review_date)}</span>
            {new Date(asset.review_date) < new Date() && (
              <AlertTriangle className="h-3 w-3 text-amber-600" />
            )}
          </div>
        ) : '-';
      case 'review_period':
        return asset.review_period ? (
          <Badge variant="outline">{formatReviewPeriod(asset.review_period)}</Badge>
        ) : '-';
      case 'created_at':
        return asset.created_at ? formatDate(asset.created_at) : '-';
      case 'updated_at':
        return asset.updated_at ? formatDate(asset.updated_at) : '-';
      case 'actions':
        return (
          <div className="flex space-x-1">
            <Button variant="outline" size="sm" onClick={() => onViewAsset(asset)}>
              <Eye className="h-3 w-3" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onEditAsset(asset)}>
              <Edit className="h-3 w-3" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => asset.id && onDeleteAsset(asset.id)}
              disabled={!asset.id}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        );
      default:
        return '-';
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {/* Checkbox column header */}
          <TableHead className="w-12">
            <Checkbox
              checked={getMasterCheckboxState()}
              onCheckedChange={(checked: boolean) => handleMasterCheckboxChange(checked)}
              aria-label={
                selectAllMode === 'all' 
                  ? `Zrušit výběr všech ${totalFilteredAssets} assets`
                  : `Vybrat všech ${totalFilteredAssets} vyfiltrovaných assets`
              }
            />
          </TableHead>
          {visibleColumns.map((column) => {
            const columnDef = COLUMN_DEFINITIONS[column as keyof typeof COLUMN_DEFINITIONS];
            if (!columnDef) return null;

            return (
              <TableHead
                key={column}
                sortable={columnDef.sortable}
                sorted={sortConfig.key === column ? sortConfig.direction : null}
                onSort={() => columnDef.sortable && onSort(column)}
                filterable={columnDef.filterable}
                filterValue={columnFilters[column] || ''}
                onFilterChange={(value) => columnDef.filterable && onColumnFilter(column, value)}
                onClearFilter={() => columnDef.filterable && onClearColumnFilter(column)}
                columnName={column}
              >
                {columnDef.label}
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {assets.map((asset) => (
          <TableRow key={asset.id}>
            {/* Checkbox column cell */}
            <TableCell>
              {asset.id && (
                <Checkbox
                  checked={selectedAssetIds.has(asset.id)}
                  onCheckedChange={(checked: boolean) => onSelectAsset(asset.id!, checked)}
                  aria-label={`Vybrat asset ${asset.name}`}
                />
              )}
            </TableCell>
            {visibleColumns.map((column) => (
              <TableCell key={column}>
                {renderCellContent(asset, column)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default AssetsTable;
